import React, { useMemo } from 'react';
import { Box, Flex, Image, Link, Td, Text, Tr, useMediaQuery } from '@chakra-ui/react';
import moment from 'moment';
import i18n from 'i18next';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { TransactionAvatar } from './transactionAvatar';
import { ITransaction } from './transactionsList';
import { DetailInfo } from './DetailInfo/detailInfo';
import { Content } from './DetailInfo/Content/content';
import { IGame } from '../../providers/Games/useGames';
import { useNavigate } from 'react-router-dom';
import { blockchainConfig } from '../../blockchain-config';
import { transactionScanUri } from '../Explorer/transactionResult';
import { IUser } from 'src/providers/User/useUser';
import { CHAKRA_MAX_WIDTH_MOBILE } from 'src/utils/themer';
import { Color } from 'src/theme/color';
import { UserIcon } from '../../common/components/icons/user';
import { GameIcon } from '../../common/components/icons/game';
import { TransactionMobileInfoIcon } from '../../common/components/icons/transaction_mobile_info';

export enum TransactionType {
	HASH,
	GAME,
}

enum Type {
	FULL,
	MINIMAL,
}

enum ImageRepresentationType {
	USER,
	GAME,
}

const ImageRepresentation = ({ type }: { type: ImageRepresentationType }) => {
	const typeAsset: { [p: number]: () => React.ReactNode } = {
		[ImageRepresentationType.USER]: () => <UserIcon />,
		[ImageRepresentationType.GAME]: () => <GameIcon />,
	};

	return <Flex margin="auto">{typeAsset[type]()}</Flex>;
};

export interface ITransactionElement {
	isBorder: boolean;
	data: ITransactionElementData;
	customTheme?: IGame['colors'];
}

export interface ITransactionElementData extends ITransaction {
	gameData: Pick<IGame, 'media' | 'name'>;
	userData: Pick<IUser, 'nickname'>;
}

export const TransactionElement = ({ isBorder, data, customTheme }: ITransactionElement) => {
	const [isMobile] = useMediaQuery(CHAKRA_MAX_WIDTH_MOBILE);

	const history = useNavigate();
	const navigateGame = () => {
		history(`/game/${data.token}`);
	};

	const typeOptions: { [p: number]: () => React.ReactNode } = {
		[Type.FULL]: () => (
			<Tr
				border="none"
				color="text"
			>
				<Td borderColor={isBorder ? '#21242B' : 'transparent'}>
					<TransactionAvatar
						type={TransactionType.GAME}
						subject={{ address: data.token, icon: data.gameData.media.logoUrl }}
						handleInfo={navigateGame}
					/>
				</Td>
				<Td borderColor={isBorder ? '#21242B' : 'transparent'}>
					<Flex
						flexDirection="column"
						gridGap="6px"
						alignItems="flex-start"
					>
						<Text
							fontSize="12px"
							fontWeight="600"
							lineHeight="15px"
						>
							Purchase
						</Text>
						<Text
							color="text"
							fontSize="10px"
							lineHeight="12px"
							maxWidth="7em"
							textOverflow="ellipsis"
							whiteSpace="nowrap"
							overflow="hidden"
						>
							{moment(data.time).locale(i18n.language).fromNow()}
						</Text>
					</Flex>
				</Td>
				<Td borderColor={isBorder ? '#21242B' : 'transparent'}>
					<Flex
						justifyContent="space-between"
						alignItems="center"
					>
						<DetailInfo
							data={data}
							customTheme={customTheme}
						/>
						<Link href={transactionScanUri[blockchainConfig.NETWORK_DEFAULT.ID](data.id)}>
							<InfoOutlineIcon
								color="text"
								cursor="pointer"
								_hover={{ color: Color.ACTION }}
							/>
						</Link>
					</Flex>
				</Td>
			</Tr>
		),
		[Type.MINIMAL]: () => (
			<Tr
				color="text"
				padding="0"
				background={customTheme ? 'transparent' : '#131313'}
				fontSize="10px"
				lineHeight="13px"
				width="100%"
				display="flex"
				_first={{ borderRadius: '7px 7px 0 0', borderTop: `4px solid ${Color.BACKGROUND}` }}
			>
				<Td
					padding="12px 10px"
					width="100%"
					display="flex"
					flexDirection="column"
				>
					<Flex
						width="100%"
						justifyContent="space-between"
						alignItems="center"
					>
						<Flex
							gridGap="8px"
							alignItems="center"
						>
							<Image
								width="22px"
								height="22px"
								borderRadius="3px"
								src={data.gameData.media.logoUrl}
							/>
							<Text fontWeight="600">Purchase</Text>
							<Text>{moment(data.time).locale(i18n.language).fromNow()}</Text>
						</Flex>
						<Link href={transactionScanUri[blockchainConfig.NETWORK_DEFAULT.ID](data.id)}>
							<Box
								cursor="pointer"
								_hover={{ color: 'action' }}
							>
								<TransactionMobileInfoIcon />
							</Box>
						</Link>
					</Flex>
					<Flex
						gridGap="8px"
						alignItems="center"
					>
						<Flex
							width="22px"
							height="22px"
							alignContent="center"
							justifyContent="center"
						>
							<ImageRepresentation type={ImageRepresentationType.USER} />
						</Flex>
						<Content
							content={{ user: { address: data.user, name: data.userData.nickname } }}
							customTheme={customTheme}
						/>
					</Flex>
					<Flex
						gridGap="8px"
						alignItems="center"
					>
						<Flex
							width="22px"
							height="22px"
							alignContent="center"
							justifyContent="center"
						>
							<ImageRepresentation type={ImageRepresentationType.GAME} />
						</Flex>
						<Content
							content={{ game: { address: data.token, name: data.gameData.name, amount: data.value } }}
							customTheme={customTheme}
						/>
					</Flex>
				</Td>
			</Tr>
		),
	};

	const connectType = useMemo(() => (isMobile ? Type.MINIMAL : Type.FULL), [isMobile]);

	return <>{typeOptions[connectType]()}</>;
};
